import { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Lock } from "lucide-react";
import { Logo } from "./Logo";

// NAV_LINKS order matches the visual order in the header.
// `anchor` values are in-page section ids on the landing page.
const NAV_LINKS = [
  { label: "Industries", anchor: "industries" },
  { label: "Process", anchor: "workflow" },
  { label: "About Us", to: "/about" },
];

export function Navbar({ onCta }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const onLanding = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // If we land on "/" with an anchor hash (e.g. from clicking "Industries"
  // while on /about), scroll the requested section into view once the page
  // has mounted.
  useEffect(() => {
    if (onLanding && location.hash) {
      const id = location.hash.slice(1);
      const el = document.getElementById(id);
      if (el) {
        // Delay a tick so layout has settled after route change
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
      }
    }
  }, [onLanding, location.hash, location.pathname]);

  const goToAnchor = useCallback((anchor) => {
    setOpen(false);
    if (onLanding) {
      const el = document.getElementById(anchor);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      // Route to landing with the hash; the effect above will scroll on arrival.
      navigate(`/#${anchor}`);
    }
  }, [onLanding, navigate]);

  const goHome = useCallback(() => {
    setOpen(false);
    if (onLanding) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  }, [onLanding, navigate]);

  const renderNavItem = (l, cls, testid) => {
    if (l.to) {
      return (
        <Link key={l.label} to={l.to} onClick={() => setOpen(false)} className={cls} data-testid={testid}>
          {l.label}
        </Link>
      );
    }
    return (
      <button
        key={l.label}
        type="button"
        onClick={() => goToAnchor(l.anchor)}
        className={cls}
        data-testid={testid}
      >
        {l.label}
      </button>
    );
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-colors ${
        scrolled ? "bg-white/90 backdrop-blur-md border-b border-brand-navy/5" : "bg-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 md:h-24 flex items-center justify-between lg:grid lg:grid-cols-3 lg:items-center">
        {/* Left — logo */}
        <button
          type="button"
          onClick={goHome}
          className="flex items-center bg-transparent border-none p-0 cursor-pointer"
          data-testid="nav-logo-link"
          aria-label="Vanguard Beacon — home"
        >
          <Logo tone="dark" size="sm" />
        </button>

        {/* Center — links */}
        <nav className="hidden lg:flex items-center justify-center gap-12">
          {NAV_LINKS.map((l) =>
            renderNavItem(
              l,
              "text-[11px] uppercase tracking-[0.25em] font-medium text-brand-navy/70 hover:text-brand-orange transition-colors bg-transparent border-none p-0 cursor-pointer",
              `nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`,
            )
          )}
        </nav>

        {/* Right — CTA */}
        <div className="flex items-center justify-end gap-4">
          <button
            type="button"
            onClick={onCta}
            className="hidden sm:inline-flex items-center justify-center rounded-full border border-brand-navy text-brand-navy px-6 py-2.5 text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-brand-navy hover:text-brand-cream transition-colors"
            data-testid="nav-cta-book-review"
          >
            Book Review
          </button>
          <button
            type="button"
            className="lg:hidden text-brand-navy p-2"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-brand-navy/10" data-testid="nav-mobile-menu">
          <div className="px-6 py-5 flex flex-col gap-4">
            {NAV_LINKS.map((l) =>
              renderNavItem(
                l,
                "text-xs uppercase tracking-[0.25em] font-medium text-brand-navy/80 hover:text-brand-orange text-left bg-transparent border-none p-0 cursor-pointer",
                `nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`,
              )
            )}
            <Link
              to="/admin/login"
              onClick={() => setOpen(false)}
              aria-label="Admin sign in"
              className="text-brand-navy/40 hover:text-brand-navy/80 self-start"
              data-testid="nav-mobile-admin-link"
            >
              <Lock size={14} strokeWidth={1.5} />
            </Link>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onCta?.();
              }}
              className="mt-2 inline-flex items-center justify-center rounded-full border border-brand-navy text-brand-navy px-6 py-2.5 text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-brand-navy hover:text-brand-cream transition-colors"
              data-testid="nav-mobile-cta"
            >
              Book Review
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
